import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  CircularProgress,  
  IconButton,  
  Menu,  
  MenuItem,  
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../../../components/hooks/useTableSortInit';
import TableTimeStamps from '../../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../../components/utils/Copyable';
import TopBar from '../../../../../../components/utils/TopBar';
import NA from '../../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { 
  GetPaymentPurposesBIZcoreCC 
} from '../../../../../../redux/actions/business/core/currencyCloudActions';

import PaymentPurposesModal from './PaymentPurposesModal';

import global from "../../../../../../global.scss";

const PaymentPurposesPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.cc"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [paymentPurposesModalOpen, setPaymentPurposesModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [paymentPurposesList, setPaymentPurposesList] = useState([]);
  const [paymentPurposesTotal, setPaymentPurposesTotal] = useState(0);
  
  const [paymentPurposesMenuOpen, setPaymentPurposesMenuOpen] = useState(false);
  const [paymentPurposesMenuAnchor, setPaymentPurposesMenuAnchor] = useState(null);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  // searching from a later page would land on an empty result set
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

//   {
//     "id": "d4a63104-6d6a-4c95-af8a-c219fc634c1d",
//     "ref_currency_id": {
//         "id": "98814f7e-7900-40ac-a6db-6feb1fb4229b",
//         "name": "Indian Rupee",
//         "code": "INR",
//         "symbol": "₹",
//         "decimal_position": 2
//     },
//     "code": "advisor_fees",
//     "description": "Fees for advisory, technical, academic or specialist assistance",
//     "created_at": "2025-01-18T10:07:25.247199Z",
//     "updated_at": "2025-01-18T10:07:25.247231Z"
// }

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Code",
      sort: "code",
    },
    {
      label: "Description",
      sort: "description",
    },
    {
      label: "Currency",
      sort: "ref_currency_id",
    },
    {
      label: "Time stamps",
      sort: "created_at",
    },
    {
      label: "",
      sort: null
    }
  ];

  useEffect(() => {
    handleGetPaymentPurposes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatPaymentPurposesList(state.paymentPurposesList.records || []);
      setPaymentPurposesTotal(state.paymentPurposesList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatPaymentPurposesList = (list) => {
    setPaymentPurposesList(list)
  };

  async function handleGetPaymentPurposes(customLoading){
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    await dispatch(GetPaymentPurposesBIZcoreCC(page+1, rowsPerPage, query, customLoading ? customLoading : setLoading));
  };

  return (
    <Box>

      <PaymentPurposesModal 
        open={paymentPurposesModalOpen}
        setOpen={setPaymentPurposesModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetPaymentPurposes}
        />

      <TopBar
        title={"Payment Purpose"}
        search={search}
        searchPlaceholder={"Search id, code or description"}
        onSearchChange={handleSearchChange}
        button1Text={"Add Payment Purpose"}
        button1OnClick={() => {
          setUpdate({
            defaultMode: 1
          });
          setModalTitle("Add Payment Purpose");
          setPaymentPurposesModalOpen(!paymentPurposesModalOpen);
        }}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          paymentPurposesList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No payment purpose found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                paymentPurposesList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.code || <NA />}</TableCell>
                    <TableCell>{row.description || <NA />}</TableCell>
                    <TableCell>
                      ({row?.ref_currency_id?.code})&nbsp;{row?.ref_currency_id?.name}
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row?.ref_currency_id?.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                        />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setPaymentPurposesMenuAnchor(e.currentTarget);
                          setPaymentPurposesMenuOpen(true);
                          setUpdate(row);
                          setModalTitle(row.name || "");
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light']}}/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[1, 5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={paymentPurposesTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
      <Menu
        open={paymentPurposesMenuOpen}
        anchorEl={paymentPurposesMenuAnchor}
        onClose={() => setPaymentPurposesMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 2
            });
            setPaymentPurposesMenuOpen(false);
            setPaymentPurposesModalOpen(!paymentPurposesModalOpen);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default PaymentPurposesPage;